// Does this person sell on Aldoo, and for whom?
//
// The answer decides where they land after signing in and whether the account
// area offers them the vendor pages at all, so it is asked once per session
// and shared — `vendor {orgs}` answers with the organizations the caller may
// act for (empty for everybody else).
import { useEffect, useState } from "react"
import Value from "aldoo-ra/Value"
import { ListVendorOrgs } from "../AdminShared/api"

let cache = { userId: null, orgs: null, promise: null }

/** The organizations of a user, fetched once. */
export function vendorOrgsOf(userId) {
  if (!userId) return Promise.resolve([])
  if (cache.userId === userId && cache.orgs) return Promise.resolve(cache.orgs)
  if (cache.userId === userId && cache.promise) return cache.promise
  cache = {
    userId,
    orgs: null,
    promise: ListVendorOrgs()
      .then((res) => {
        cache.orgs = res?.orgs || []
        return cache.orgs
      })
      .catch(() => {
        // not knowing is not the same as knowing there are none, but the
        // caller only needs somewhere to send the person
        cache.orgs = []
        return cache.orgs
      }),
  }
  return cache.promise
}

/** Forget what was learned (after signing out, or joining an organization). */
export const forgetVendorOrgs = () => {
  cache = { userId: null, orgs: null, promise: null }
}

/**
 * `{ orgs, loading }` for the signed-in user. `orgs` is empty for a reader
 * who sells nothing, which is most people.
 */
export default function useVendorOrgs() {
  const [auth] = Value("user-credentials")
  const [state, setState] = useState({ orgs: [], loading: true })

  useEffect(() => {
    if (auth === undefined) return
    if (!auth?._id || !auth?.auth_token) {
      forgetVendorOrgs()
      setState({ orgs: [], loading: false })
      return
    }
    let alive = true
    vendorOrgsOf(auth._id).then((orgs) => alive && setState({ orgs, loading: false }))
    return () => {
      alive = false
    }
  }, [auth?._id, auth?.auth_token])

  return state
}
